import { vec2 } from "@tlaukkan/tsm";
import { Composition, CompositionElement } from "./composition";
import { Multiplayer, PlayerConstants, ShopConstants } from "./constants";
import { Game } from "./game";
import { GameElement } from "./gameElementTypes";
import { cellLocations } from "./generated/assetLocations";
import { RewardCellClass } from "./immuneCellTiers";
import { TrainingLevel } from "./playerAccount";
import { ArrayVec2, generateSyncId } from "./synchronization";
import { pixelsToMeters, rotateAround } from "./utils";


export class Player {
	isBot = false;
	id: number = -1;
	name: string = "";
	hp: number = -1; //initialized later
	maxHp: number = -1;
	maxFunds: number = PlayerConstants.StartingFunds;
	currentFunds: number = PlayerConstants.StartingFunds;
	bonusIncome: number = 0;
	level: number = PlayerConstants.StartingLevel;
	experience: number = PlayerConstants.StartingExp;
	freezeShop: boolean = false;
	immuneCells: Composition = [];
	hpCells: Composition = [];
	enemyCells: Composition = [];
	tiebreakerCells: Composition = [];
	infectedCells: Composition = [];
	droppedProteins: CompositionProtein[] = [];
	maxCapacity: number = 3;
	offer: GameElement[] = [];
	rewardOffer: GameElement[] = [];
	rewardOptions: GameElement[] = [];
	selectedRewards: GameElement[] = [];
	spawnPoint!: ArrayVec2;
	bloodCellCenterPoint!: ArrayVec2;
	sharedLanes: Array<{ lane: number, otherPlayerId: number, isOwner: boolean }> = [];
	isMirrored: boolean = false;
	shopSeed: number = Math.floor(Math.random() * 0xFFFFFFFF); //random 32-bit integer, so it can be serialized and deserialized without loss of precision
	//Treat as serializable, no member functions.

	constructor() {
	}
}

export interface PlayerState extends Player {
	currentState: string,
	round: number,
	opponents: Player[],
}

export interface CompositionProtein {
	type: GameElement,
	position: ArrayVec2,
	id: number,
	dropperId: number,
	roundDropped: number
}

// export interface SharedEnemyCell {
// 	id: number;
// 	ownerPlayerId: number;
// 	position: ArrayVec2,
// 	rotation: number,
// 	type: GameElement,
// 	waveNum: number,
// 	waveCenter: ArrayVec2
// }

export function createPlayer(id: number, name: string) {
	const player = new Player();
	player.id = id;
	player.name = name;
	return player;
}

export function getPlayerRadians(id: number, totalPlayers: number) {
	return (Math.PI / 180) * (360 / totalPlayers) * id;
}


export function initializePlayer(player: Player, game: Game, trainingLevel: TrainingLevel) {
	const numPlayers = game.requiredPlayers;
	if (numPlayers === 1) {
		player.spawnPoint = new vec2([0, 0]).xy;
	}
	else {
		const playerRadian = getPlayerRadians(player.id, numPlayers);
		player.spawnPoint = rotateAround(new vec2([1, 0]).scale(Multiplayer.PlayerSpawnPointOffset), playerRadian, vec2.zero).xy;
	}

	player.bloodCellCenterPoint = player.spawnPoint;
	player.selectedRewards = [];

	const startingHp = PlayerConstants.StartingHpByTrainingLevel[trainingLevel];
	player.maxHp = startingHp;
	player.hp = startingHp;
	player.hpCells = [];

	//TODO: Have this stuff derived from maxHP
	let columns = 7;
	let rows = 6;
	//Hack for 16 hp in particular 
	if (player.maxHp <= 16) {
		columns = 5
		rows = 4
	}

	const width = pixelsToMeters(cellLocations.bloodcell.width);
	const spawnPoint = new vec2(player.spawnPoint).add(new vec2([width / 2, (width * rows) / -2]));
	const size = width / 2;
	const height = size * Math.sqrt(3);

	let count = 0;
	tileHexes(rows, columns, width, (x: number, y: number) => {
		if (count >= player.maxHp) return;
		++count;
		const cell = {
			type: GameElement.BLOODCELL,
			position: [spawnPoint.x + x - (width / 2), spawnPoint.y + y + (height / 2)],
			rotation: 0,
			id: generateSyncId(),
			combined: 0
		} as CompositionElement
		game.addCompositionElement(player.hpCells, cell);
	});
	function tileHexes(rows: number, columns: number, width: number, makeHex: (x: number, y: number) => void) {
		const horizontal = width * (3 / 4);

		for (let i = 0; i <= columns; ++i) {
			const distanceFromCenter = Math.ceil(i / 2);
			const col = i % 2 ? distanceFromCenter : -distanceFromCenter;
			const rowsThisCol = rows - distanceFromCenter;
			const columnOffsetToFit = distanceFromCenter * height / 2;
			for (let row = 0; row < rowsThisCol; ++row) {
				const x = col * horizontal;
				const y = row * height + columnOffsetToFit;
				makeHex(x, y);
			}
		}
	}
}

export function couldPromote(player: Player, immuneCell: GameElement) {
	const hasAnyNonElite = player.immuneCells.some(cell => !cell.promoted);
	const likeCells = findAlikeElements(player, immuneCell).length > 0 || false
	const isWild = immuneCell === GameElement.WILDCARD;
	return likeCells || (isWild && hasAnyNonElite);
}

export function canAffordCell(player: Player, isReward: boolean) {
	return isReward || canAffordImmuneCell(player);
}

export function canPlaceCell(player: Player, cell: GameElement,) {
	return hasRoom(player) || couldPromote(player, cell);
}

export function income(player: Player) {
	player.maxFunds = Math.min(player.maxFunds + PlayerConstants.Income, PlayerConstants.MaxStartingMoney);
	player.currentFunds = player.maxFunds + player.bonusIncome;
	player.bonusIncome = 0;

	gainExperience(player, PlayerConstants.ExperiencePerRound);
}

export function canBuyAntibody(player: Player) {
	return canAffordImmuneCell(player) && hasRoom(player);
}

export function canAffordImmuneCell(player: Player) {
	return player.currentFunds >= PlayerConstants.ImmuneCellCost;
}

export function hasRoom(player: Player) {
	return (player.immuneCells.length) < player.maxCapacity;
}

export function canBuyExperience(player: Player) {
	return player.currentFunds >= PlayerConstants.ExperienceCost
		&& player.level < PlayerConstants.MaxLevel;

}

export function canRefreshShop(player: Player) {
	return player.currentFunds >= 1;
}

export function spend(player: Player, amount: number) {
	return player.currentFunds -= amount;
}

export function sell(player: Player, element: CompositionElement) {
	let sellValue = 1;
	if (element?.changedStats) {
		sellValue += element.changedStats.sellValue || 0;
	}
	return player.currentFunds = player.currentFunds + sellValue;
}

export function canCombine(player: Player, target: CompositionElement, combined: { type: GameElement, id?: number, promoted?: boolean }) {
	if (combined?.promoted || target.promoted) {
		return false;
	}
	if (combined?.type === GameElement.WILDCARD || target.type === GameElement.WILDCARD) {
		return true;
	}
	if ((combined as any).position === undefined) { //hacky check if this is a buy preview
		return target.type === combined.type &&
			player.immuneCells.some(cell => cell.id === target.id)
	}
	return target.type === combined.type &&
		player.immuneCells.some(cell => cell.id === target.id) &&
		player.immuneCells.some(cell => cell.id === combined.id)
}

export function gainExperience(player: Player, experience = PlayerConstants.ExperienceGain): boolean {
	let hasLeveledUp = false;
	if (player.level < PlayerConstants.MaxLevel) {
		player.experience += experience;
		const expToLevel = PlayerConstants.ExperienceLevels[player.level - 1];
		if (player.experience >= expToLevel) {
			player.level++;
			hasLeveledUp = true;
		}
		player.maxCapacity = PlayerConstants.CapacityPerLevel[player.level - 1];
	}
	return hasLeveledUp;
}

export function generateOffer(player: Player, offer: GameElement[]) {
	player.offer = offer;
}

export function findAlikeElements(player: Player, type: GameElement, allowPromoted = false) {
	return player.immuneCells.filter(cell => cell.type === type && (allowPromoted || !cell.promoted));
}

export function canGroupPromote(group: Array<CompositionElement>, extraCount = 0) {
	return group.filter(cell => cell.combined < 2).map(cell => cell.combined + 1).reduce((a, b) => a + b, 0) >= (ShopConstants.PromotionNumber - (extraCount));
}

export function isRewardCell(type: GameElement) {
	// TODO: Update this in case we have different reward tiers
	return RewardCellClass[0].indexOf(type) >= 0;
}